import { ArrowDown01Icon } from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'

export type UserMenuUser = {
  name: string
  email?: string
  /** Subtítulo no seletor de contas (ex.: cargo). Se omitido, usa email. */
  role?: string
  image?: string
  /** Iniciais ou texto quando não há imagem. */
  fallback?: string
  /** circle (padrão) ou square para logos de workspace. */
  avatarShape?: 'circle' | 'square'
}

export type UserMenuProfile = UserMenuUser & {
  id: string
}

function userFallback(user: UserMenuUser) {
  if (user.fallback) {
    return user.fallback
  }
  const parts = user.name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) {
    return '?'
  }
  if (parts.length === 1) {
    return (parts[0] ?? '').slice(0, 2).toUpperCase()
  }
  return `${parts[0]?.[0] ?? ''}${parts[parts.length - 1]?.[0] ?? ''}`.toUpperCase()
}

function userSubtitle(user: UserMenuUser) {
  return user.role ?? user.email
}

function UserMenuAvatar({
  user,
  size = 'sm',
}: {
  user: UserMenuUser
  size?: 'sm' | 'default' | 'lg'
}) {
  const square = user.avatarShape === 'square'

  return (
    <Avatar
      size={size}
      className={cn(
        square &&
          'rounded-lg after:rounded-lg [&_[data-slot=avatar-fallback]]:rounded-lg [&_[data-slot=avatar-image]]:rounded-lg'
      )}
    >
      {user.image ? <AvatarImage src={user.image} alt="" /> : null}
      <AvatarFallback>{userFallback(user)}</AvatarFallback>
    </Avatar>
  )
}

function UserMenu(props: React.ComponentProps<typeof DropdownMenu>) {
  return <DropdownMenu {...props} />
}

type UserMenuTriggerProps = Omit<React.ComponentProps<typeof Button>, 'children'> & {
  user: UserMenuUser
  /** false: só o avatar (use aria-label ou name para acessibilidade). */
  showName?: boolean
  /** compact: altura alinhada a ThemeToggle (h-8) e avatar menor. */
  density?: 'default' | 'compact'
}

function UserMenuTrigger({
  user,
  showName = true,
  density = 'default',
  className,
  variant = 'ghost',
  'aria-label': ariaLabel,
  ...props
}: UserMenuTriggerProps) {
  const label = ariaLabel ?? user.name
  const compact = density === 'compact'

  return (
    <DropdownMenuTrigger
      render={
        <Button
          variant={variant}
          className={cn(
            'h-auto max-w-[14rem] gap-2 rounded-lg px-1.5 py-1.5',
            compact && showName && 'h-8 max-w-[11rem] gap-1.5 py-0',
            compact && !showName && 'size-8 px-0',
            !compact && !showName && 'size-8 px-0',
            className
          )}
          aria-label={!showName ? label : undefined}
          {...props}
        />
      }
    >
      <UserMenuAvatar user={user} size={compact || showName ? 'sm' : 'default'} />
      {showName ? (
        <>
          <span
            className={cn(
              'min-w-0 flex-1 truncate text-left font-medium',
              compact ? 'text-xs' : 'text-sm'
            )}
          >
            {user.name}
          </span>
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            strokeWidth={2}
            className={cn('shrink-0 text-muted-foreground', compact ? 'size-3.5' : 'size-4')}
            aria-hidden
          />
        </>
      ) : null}
    </DropdownMenuTrigger>
  )
}

function UserMenuContent({
  className,
  align = 'end',
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuContent>) {
  return (
    <DropdownMenuContent
      data-slot="user-menu-content"
      align={align}
      sideOffset={sideOffset}
      className={cn('w-72 p-1', className)}
      {...props}
    />
  )
}

function UserMenuAccount({ user, className }: { user: UserMenuUser; className?: string }) {
  return (
    <div
      data-slot="user-menu-account"
      className={cn('flex items-center gap-2.5 px-2 py-2', className)}
    >
      <UserMenuAvatar user={user} size="default" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium">{user.name}</p>
        {userSubtitle(user) ? (
          <p className="truncate text-xs text-muted-foreground">{userSubtitle(user)}</p>
        ) : null}
      </div>
    </div>
  )
}

type UserMenuProfileSwitchProps = {
  profiles: UserMenuProfile[]
  value: string
  onValueChange: (profileId: string) => void
  label?: string
}

function UserMenuProfileSwitch({
  profiles,
  value,
  onValueChange,
  label = 'Trocar conta',
}: UserMenuProfileSwitchProps) {
  return (
    <div data-slot="user-menu-profile-switch" className="flex flex-col gap-0.5 px-1 pb-0.5">
      <p className="px-2 pt-1.5 pb-1 text-xs font-medium text-muted-foreground">{label}</p>
      <RadioGroup value={value} onValueChange={onValueChange} className="gap-0.5">
        {profiles.map((profile) => {
          const inputId = `user-menu-profile-${profile.id}`
          const subtitle = userSubtitle(profile)
          const selected = value === profile.id

          return (
            <label
              key={profile.id}
              htmlFor={inputId}
              className={cn(
                'flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2.5 transition-colors hover:bg-muted/60',
                selected && 'bg-muted'
              )}
            >
              <UserMenuAvatar user={{ ...profile, avatarShape: 'square' }} size="default" />
              <div className="pointer-events-none min-w-0 flex-1">
                <p className="truncate text-sm leading-snug font-medium">{profile.name}</p>
                {subtitle ? (
                  <p className="truncate text-xs leading-snug text-muted-foreground">{subtitle}</p>
                ) : null}
              </div>
              <RadioGroupItem id={inputId} value={profile.id} className="ml-auto shrink-0" />
            </label>
          )
        })}
      </RadioGroup>
    </div>
  )
}

const UserMenuGroup = DropdownMenuGroup
const UserMenuItem = DropdownMenuItem
const UserMenuSeparator = DropdownMenuSeparator
const UserMenuLabel = DropdownMenuLabel

export {
  UserMenu,
  UserMenuAccount,
  UserMenuAvatar,
  UserMenuContent,
  UserMenuGroup,
  UserMenuItem,
  UserMenuLabel,
  UserMenuProfileSwitch,
  UserMenuSeparator,
  UserMenuTrigger,
}
